import {
  CartLineItemContainer,
  OnUpdateCallback,
} from '@app/components/LineItem';
import {
  getEstimatedShippingValue,
  useCartAndDeliveryDatesDataContext,
  useCurrency,
  useUpdateCartLineItem,
} from '@app/data/clients/cartCheckoutClient';
import { Item } from '@app/types/monolith/cart';
import {
  monolithItemToAnalyticsItem,
  trackGA4ViewCart,
} from '@app/utils/analytics';
import { useIntl } from '@redbubble/next/intl';
import { ExperimentConfig } from '@redbubble/next-amplitude';
import { AnalyticsPageTypeProperty } from '@redbubble/next-analytics/gtm';
import { useAmplitudeExperiment } from '@redbubble/next-provider/amplitude';
import { useMemo, useState } from 'react';
import { Message } from '../Messages/types';
import { LineItemsList } from './LineItemsList';
import { getUpdateLineItemErrorMessage } from './utils';

export interface CartLineItemListContainerProps {
  onUpdate?: OnUpdateCallback;
  narrow?: boolean;
  editable?: boolean;
  setLineItemUpdateErrors?: (errors: Message[]) => void;

  // TODO(jj): We should not need this 'loading' prop.
  // The loading state can be derived from
  // the useCartAndDeliveryDatesDataContext hook.
  // However, for historical reasons, the pages
  // using using this component derive loading states in different ways.
  //
  // The Fix in this case, is to figure out exactly
  // how we represent a loading state
  // which works for all pages, and implement it inside
  // this component.
  loading?: boolean;
  analyticsPageType: AnalyticsPageTypeProperty;
  showInclVatText?: boolean;
}

const toLineItemError = (error: Message, itemId: string): Message => ({
  ...error,
  itemId,
});

// XXX(jj): Sorting cart items by id desc lives here because the monolith
// cart API doesn't return items in a stable order. Other consumers
// (CartItemsTray, CheckoutSteps, CartSummaryStep) read cart.items in raw
// API order, so the same cart can render in different orders across the
// app. The fix is to apply this sort once inside the cart context (or the
// cartCheckoutClient layer) so cart.items is always pre-sorted by the time
// any component reads it — at which point this helper, useSortedCartItems
// below, and the post-update analytics sort all go away.
const sortCartItemsForDisplay = (items: Item[]) =>
  items.toSorted((a, b) => b.id - a.id);

const useSortedCartItems = () => {
  const { cart } = useCartAndDeliveryDatesDataContext();

  const items = cart?.items || [];

  return useMemo(() => sortCartItemsForDisplay(items), [items]);
};

export const CartLineItemListContainer = ({
  narrow,
  loading,
  editable,
  analyticsPageType,
  setLineItemUpdateErrors,
}: CartLineItemListContainerProps) => {
  const cartContext = useCartAndDeliveryDatesDataContext();
  const { cart, cartLoading } = cartContext;
  const intl = useIntl();
  const currency = useCurrency();

  const { handler: handleUpdateLineItem } = useUpdateCartLineItem(cartContext);

  const [inlineErrorsByLineItemId, setInlineErrorsByLineItemId] = useState<
    Record<string, string>
  >({});

  const isCartShippingRemovalTreatment =
    useAmplitudeExperiment(ExperimentConfig.CartShippingRemoval) ===
    'treatment';
  const showInclVatText =
    isCartShippingRemovalTreatment && !cart?.summary.show_tax_separately;
  const [disableAddButtonByLineItemId, setDisableAddButtonByLineItemId] =
    useState<Record<string, boolean>>({});

  /**
   * XXX(cy): There's potential race condition where the removal http request completes after the add http request,
   * causing the Cart Tray to reflect outdated cart state from the removal response.
   *
   * This can happen when the user updates the cart line item, immediately closes the Cart Tray, then opens the Cart
   * Tray again by adding the same item to Cart.
   */
  const onUpdate = async (args: {
    lineItemId: string;
    newQuantity: number;
    signal?: AbortSignal;
  }) => {
    const { lineItemId, newQuantity } = args;

    setLineItemUpdateErrors?.([]);
    setInlineErrorsByLineItemId((prev) => {
      const { [lineItemId]: currentLineItemError, ...rest } = prev;

      return rest;
    });
    setDisableAddButtonByLineItemId((prev) => ({
      ...prev,
      [lineItemId]: false,
    }));

    const { cart: updatedCart, error } = await handleUpdateLineItem({
      inventoryItemId: lineItemId,
      quantity: newQuantity,
    });

    if (error) {
      const { inlineErrors, generalErrors } = getUpdateLineItemErrorMessage(
        error,
        intl
      );
      if (inlineErrors.length) {
        setInlineErrorsByLineItemId((prev) => ({
          ...prev,
          [lineItemId]: inlineErrors[0],
        }));
        setDisableAddButtonByLineItemId((prev) => ({
          ...prev,
          [lineItemId]: true,
        }));
      }

      if (generalErrors.length) {
        setLineItemUpdateErrors?.([
          toLineItemError(generalErrors[0], lineItemId),
        ]);
      }
      // Need to throw here so that the shown quantity does not get updated in case of an error.
      // This error message is not used anywhere. See the debouncedUpdateLineItem in the LineItem.tsx
      throw new Error('update failed');
    }

    if (updatedCart) {
      const { show_tax_separately, estimated_shipping, total } =
        updatedCart.summary;
      const cartCurrency = updatedCart.currency;
      trackGA4ViewCart({
        currency: cartCurrency,
        value: total,
        couponCode: updatedCart.applied_coupon?.code,
        shipping: getEstimatedShippingValue(
          estimated_shipping,
          show_tax_separately
        ),
        items: sortCartItemsForDisplay(updatedCart.items).map((item, i) =>
          monolithItemToAnalyticsItem(
            item,
            cartCurrency,
            i,
            show_tax_separately
          )
        ),
      });
    }
  };

  const items = useSortedCartItems();

  const showTaxSeparately = cart?.summary.show_tax_separately;

  return (
    <LineItemsList loading={loading ?? (cartLoading && !cart)} narrow={narrow}>
      {items?.map((item) => {
        return (
          <li key={item.id}>
            <CartLineItemContainer
              item={item}
              showTaxSeparately={showTaxSeparately}
              currency={currency}
              analyticsPageType={analyticsPageType}
              onUpdate={onUpdate}
              editable={editable}
              narrow
              errorString={inlineErrorsByLineItemId?.[item.id]}
              disableAddButton={disableAddButtonByLineItemId?.[item.id]}
              showInclVatText={showInclVatText}
            />
          </li>
        );
      })}
    </LineItemsList>
  );
};
